/**
 * @fileoverview Debug page for store variant aliases (DimStoreVariant).
 * Access at /dashboard/debug/store-variants
 * @module features/debug/pages/StoreVariantsDebugPage
 */

import { ReactElement, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ExternalLink, RefreshCw, Search } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import {
  useStoreVariantsQuery,
  useStoreVariantsCountQuery,
  STORE_VARIANTS_QUERY_KEY,
  STORE_VARIANTS_COUNT_QUERY_KEY,
  type StoreVariant,
} from '@/features/debug/api/storeVariants'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

const PAGE_SIZE = 50

/**
 * Debug page for browsing DimStoreVariant entries.
 */
export function StoreVariantsDebugPage(): ReactElement {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Debounce search input
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value)
    // Simple debounce using setTimeout
    setTimeout(() => {
      setDebouncedSearch(value)
      setPage(0)
    }, 300)
  }, [])

  const handleRefresh = useCallback(async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: STORE_VARIANTS_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: STORE_VARIANTS_COUNT_QUERY_KEY }),
    ])
  }, [queryClient])

  const params = {
    page,
    size: PAGE_SIZE,
    search: debouncedSearch || undefined,
    sort: 'createdAt,desc',
  }

  const { data, isLoading, isError, error, isFetching } = useStoreVariantsQuery(params)
  const { data: countData } = useStoreVariantsCountQuery()

  const storeVariants = data?.content ?? []
  const totalElements = data?.totalElements ?? 0
  const totalPages = data?.totalPages ?? 1
  const isLastPage = data?.last ?? true

  return (
    <div className="mx-auto max-w-screen-2xl space-y-6 px-4 py-8 lg:px-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-foreground">Store Variants Debug</h1>
        <p className="text-sm text-muted-foreground">
          Browse DimStoreVariant entries (URL → Variant mappings from catalog sync)
        </p>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4">
        <Badge variant="secondary" className="text-sm">
          Total: {countData?.total ?? '...'} entries
        </Badge>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search path or slug..."
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isFetching}
        >
          <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-card">
        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <div className="flex flex-col items-center gap-4 p-10 text-center">
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : 'Failed to load store variants'}
            </p>
            <Button variant="outline" onClick={handleRefresh}>
              Retry
            </Button>
          </div>
        ) : storeVariants.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">
            No store variants found
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">ID</TableHead>
                <TableHead>Store</TableHead>
                <TableHead className="max-w-[300px]">Canonical Path</TableHead>
                <TableHead>Variant</TableHead>
                <TableHead className="w-[140px]">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {storeVariants.map(sv => (
                <StoreVariantRow key={sv.id} storeVariant={sv} />
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          disabled={page === 0 || isFetching}
          onClick={() => setPage(prev => Math.max(prev - 1, 0))}
        >
          Previous
        </Button>
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          Page {page + 1} of {totalPages} ({totalElements} total)
        </span>
        <Button
          variant="ghost"
          disabled={isLastPage || isFetching}
          onClick={() => setPage(prev => prev + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

interface StoreVariantRowProps {
  storeVariant: StoreVariant
}

function StoreVariantRow({ storeVariant }: StoreVariantRowProps): ReactElement {
  const sv = storeVariant
  const navigate = useNavigate()

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-'
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  const storeUrl = `https://${sv.store.platformDomain}${sv.canonicalPath}`

  const handleVariantClick = (): void => {
    navigate(`/dashboard/management/products/${sv.variant.productId}?variantId=${sv.variant.id}`)
  }

  return (
    <TableRow>
      <TableCell className="font-mono text-xs text-muted-foreground">{sv.id}</TableCell>
      <TableCell>
        <div className="space-y-0.5">
          <div className="font-medium text-sm">{sv.store.name}</div>
          <div className="text-xs text-muted-foreground">{sv.store.platformDomain}</div>
        </div>
      </TableCell>
      <TableCell className="max-w-[300px]">
        <div className="space-y-0.5">
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-1 text-xs font-mono text-foreground hover:text-primary transition-colors"
            title={storeUrl}
          >
            <code className="truncate">{sv.canonicalPath}</code>
            <ExternalLink className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
          {sv.productSlug && (
            <div className="text-xs text-muted-foreground">slug: {sv.productSlug}</div>
          )}
          {sv.externalVariantId && (
            <div className="text-xs text-muted-foreground">ext: {sv.externalVariantId}</div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={handleVariantClick}
          className="text-left space-y-0.5 hover:text-primary transition-colors cursor-pointer"
        >
          <div className="font-medium text-sm">{sv.variant.sku}</div>
          <div className="text-xs text-muted-foreground truncate max-w-[200px]" title={sv.variant.name}>
            {sv.variant.name}
          </div>
        </button>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {formatDate(sv.createdAt)}
      </TableCell>
    </TableRow>
  )
}

function TableSkeleton(): ReactElement {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 10 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  )
}

export default StoreVariantsDebugPage
